import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Alert, AlertTitle, Button, Typography, Paper } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

interface Props {
    children: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
    public state: State = {
        hasError: false,
        error: null,
        errorInfo: null
    };

    public static getDerivedStateFromError(error: Error): State {
        return {
            hasError: true,
            error,
            errorInfo: null
        };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('Uncaught error:', error, errorInfo);
        this.setState({
            error,
            errorInfo
        });
    }

    private handleReload = () => {
        window.location.reload();
    };

    public render() {
        if (this.state.hasError) {
            const isFacebookBrowser = /FBAN|FBAV|Instagram/i.test(navigator.userAgent);

            return (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: '100vh',
                        p: 3,
                        bgcolor: '#f5f5f5'
                    }}
                >
                    <Paper sx={{ maxWidth: 600, p: 3 }}>
                        <Alert severity="error" sx={{ mb: 2 }}>
                            <AlertTitle>Oops! Something went wrong</AlertTitle>
                            {isFacebookBrowser ? (
                                <>
                                    <Typography variant="body2" sx={{ mb: 2 }}>
                                        Famous Initials doesn't work properly in Facebook's in-app browser
                                        due to browser limitations.
                                    </Typography>
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        bgcolor: 'rgba(255, 255, 255, 0.9)',
                                        p: 1.5,
                                        borderRadius: 1,
                                        border: '1px solid',
                                        borderColor: 'error.main',
                                        mb: 2
                                    }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            Please tap the menu
                                        </Typography>
                                        <OpenInNewIcon sx={{ fontSize: 18, mx: 0.5 }} />
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            and select "Open in Browser"
                                        </Typography>
                                    </Box>
                                    <Typography variant="body2" color="text.secondary">
                                        This will open the game in Safari, Chrome, or your default browser
                                        where it will work perfectly.
                                    </Typography>
                                </>
                            ) : (
                                <>
                                    <Typography variant="body2">
                                        The game encountered an unexpected error. This might be due to browser
                                        compatibility issues.
                                    </Typography>
                                </>
                            )}
                        </Alert>

                        {!isFacebookBrowser && (
                            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                                <Button
                                    variant="contained"
                                    startIcon={<RefreshIcon />}
                                    onClick={this.handleReload}
                                >
                                    Reload Page
                                </Button>
                            </Box>
                        )}

                        {process.env.NODE_ENV === 'development' && this.state.error && (
                            <Box sx={{ mt: 3 }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Error Details (Development Only):
                                </Typography>
                                <Paper sx={{ p: 2, bgcolor: '#f5f5f5', overflow: 'auto', maxHeight: 200 }}>
                                    <Typography variant="caption" component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                        {this.state.error.toString()}
                                        {this.state.errorInfo && '\n\n' + this.state.errorInfo.componentStack}
                                    </Typography>
                                </Paper>
                            </Box>
                        )}
                    </Paper>
                </Box>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
